import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { Subject, combineLatest } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { AuthenticationService, PlatformService } from '@app/services';
import { PermissionService } from '@app/services/permission.service';
import { ExternalAuthConfig, OidcProvider } from '@app/models/authentification-config';
import { Location } from '@angular/common';
import { oidcMessage } from '@app/models/authentification-config'

const storageUsernameKey = 'portail-remember-login';


@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: false
})
export class LoginComponent implements OnInit, OnDestroy {

  @Input() homeCard = false;

  loginForm: FormGroup = this.formBuilder.group({
    username: ['', { validators: [Validators.required] }],
    password: ['', { validators: [Validators.required] }],
    remember: [true]
  });

  loginError: string;
  normalLoginLoading = false;
  showPass: boolean = false;

  redirecting: boolean;
  redirectingMessage: string;

  onDestroy$ = new Subject<void>();
  externalAuthConf: ExternalAuthConfig;
  oidcProvider: OidcProvider = null
  oidcMessage: oidcMessage = null
  oidcMustGoLogout: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private formBuilder: FormBuilder,
    public authService: AuthenticationService,
    public permService: PermissionService,
    public platformService: PlatformService,
    private location: Location
  ) {
  }

  ngOnInit() {

    combineLatest([this.authService.getExternalAuthConf(), this.authService.oidcMessage$])
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(([conf, msg]: [ExternalAuthConfig, oidcMessage]) => {
        this.externalAuthConf = conf
        this.oidcMessage = msg

        this.oidcProvider = this.externalAuthConf?.providers?.find(p => p.providerId === this.oidcMessage?.providerId)

        if (this.oidcMessage?.type === 'infoUserNotFound') {
          switch (this.oidcProvider?.disconnectProviderAtLogout) {
            case 'no': this.oidcMustGoLogout = false; break;
            case 'yes':
            case 'userChoice':
              this.oidcMustGoLogout = true; break;
          }
        }

      });

    const inputEmail = localStorage.getItem(storageUsernameKey);

    if (inputEmail) {
      this.loginForm.get('username').setValue(inputEmail);
    }


    if (this.route.snapshot.queryParamMap.has('redirect') && !!this.authService.currentUserValue) { // simple redirect when already logged (for reload purpose)
      this.authService.redirectAfterLogin(this.authService.currentUserValue, this.route.snapshot)
    } else { // Normal login
      this.loginForm.valueChanges.subscribe(_ => {
        this.loginForm.setErrors(null)
        this.loginError = null;
      })
    }
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  login() {
    this.loginError = null;
    if (this.loginForm.invalid) {
      return;
    }

    this.normalLoginLoading = true;
    this.authService.login(this.loginForm.value)
      .pipe(finalize(() => {
        this.loginForm.markAsPristine();
        this.normalLoginLoading = false;
      }))
      .subscribe({
        next: user => {

          if (user && user.token) {
            if (this.loginForm.get('remember').value) {
              localStorage.setItem(storageUsernameKey, this.loginForm.value?.username);
            }
            this.authService.redirectAfterLogin(user, this.route.snapshot)
          }

        }, error: error => {
          this.loginError = error;
        }
      });
  }

  clearOidcMessage(redirectToLogin = false) {
    this.oidcMessage = null;
    this.authService.setOidcMessage(null)
    if (redirectToLogin) {
      if (this.oidcMustGoLogout) {
        this.router.navigate(['/logout'])
      } else {
        this.location.go('/login') // modify url without change route
      }
    }
  }

}

